import React, { useId } from 'react'

import * as SC from './CheckboxGroupCustomFormFieldStyles'
import { CheckboxGroupCustomFormFieldProps } from './types'
import { BaseCheckboxGroupCustomOption, CheckboxGroupCustomValueType } from '../../types/types'
import FormItem from '../../atoms/FormItem/FormItem'

const defaultOptionRender = <OptionPropsType extends BaseCheckboxGroupCustomOption = BaseCheckboxGroupCustomOption>(
	option: OptionPropsType,
	isChecked: boolean
) => {
	return <SC.CheckWrapper $checked={isChecked}>{option.label}</SC.CheckWrapper>
}

const CheckboxGroupCustomFormField = <
	ValuePropsType extends CheckboxGroupCustomValueType = string[],
	OptionPropsType extends BaseCheckboxGroupCustomOption = BaseCheckboxGroupCustomOption
>(
	props: CheckboxGroupCustomFormFieldProps<ValuePropsType, OptionPropsType>
) => {
	const {
		options,
		input: { value, name, onChange },
		meta: { invalid, error },
		singleSelect,
		optionRender,
		afterChange,
		alignment,
		disabled,
		readOnly = false,
		allowUnselect = true,
		optionAsValue = false,
		gap = '8px',
		label,
		labelScreenReaderOnly,
		required
	} = props

	const groupId = useId()
	const invalidStatus = !!(error || invalid)

	return (
		<FormItem asFieldset label={label} labelScreenReaderOnly={labelScreenReaderOnly} required={required} error={invalidStatus && error ? error : undefined}>
			<SC.CheckGroup $alignment={alignment} $gap={gap}>
				{options.map((option) => {
					const isChecked = !!value.find((v) => {
						if (typeof v === 'object') {
							return v.value === option.value
						}
						return v === option.value
					})
					const isDisabled = !!(option.disabled || disabled)
					const optionId = `${groupId}-${option.key}`

					return (
						<SC.CheckboxWrapper key={option.key}>
							<SC.Checkbox
								type={'checkbox'}
								id={optionId}
								name={name}
								value={option.value}
								checked={isChecked}
								disabled={isDisabled}
								onChange={() => {
									if (isDisabled || readOnly) {
										return
									}

									let newValue: ValuePropsType = value

									if (
										!allowUnselect &&
										isChecked &&
										value.length === 1 &&
										(typeof value[0] === 'object' ? value[0].value === option.value : value[0] === option.value)
									) {
										return
									}

									if (isChecked) {
										newValue = (
											Array.isArray(value)
												? value.filter((v) => {
														if (typeof v === 'object') {
															return v.value !== option.value
														}
														return v !== option.value
													})
												: value
										) as ValuePropsType
									} else {
										newValue = (
											singleSelect ? [optionAsValue ? option : option.value] : [...(value || []), optionAsValue ? option : option.value]
										) as ValuePropsType
									}

									onChange(newValue)

									if (afterChange) {
										afterChange(newValue, option)
									}
								}}
							/>
							<SC.Label $checked={isChecked} htmlFor={optionId}>
								{optionRender ? optionRender(option, isChecked, isDisabled) : defaultOptionRender(option, isChecked)}
							</SC.Label>
						</SC.CheckboxWrapper>
					)
				})}
			</SC.CheckGroup>
		</FormItem>
	)
}

export default CheckboxGroupCustomFormField
